/**
 * useDignityChat — State management for the Dignity chat experience.
 *
 * Manages the full conversation lifecycle:
 * 1. Intake phase: Conversational data collection (no API calls, zero latency)
 * 2. Payment phase: Inline payment gate in chat thread
 * 3. AI chat phase: Post-payment conversation with mock AI (Phase 2) / real Claude (Phase 3)
 * 4. Finalize: Generate FinalizedScenario for review step
 *
 * Message counting (15 limit) only starts AFTER payment.
 */

import { useState, useCallback, useRef } from 'react';
import type { LocalScenarioProcedure } from '@/lib/scenarioStorage';
import type { IntakeData } from '@/components/scenario-builder/AiIntakeForm';
import type { FinalizedScenario } from '@/hooks/useAiChat';
import type { QuickReply } from '@/components/scenario-builder/DignityIntakeCollector';
import {
  type IntakePhase,
  getQuestion,
  getNextPhase,
  buildIntakeData,
} from '@/components/scenario-builder/DignityIntakeCollector';

// Re-export for convenience
export type { FinalizedScenario } from '@/hooks/useAiChat';

export interface DignityMessage {
  id: string;
  role: 'user' | 'assistant' | 'system';
  content: string;
  quickReplies?: QuickReply[];
  isPaymentGate?: boolean;
  timestamp: number;
}

export type DignityPhase = 'intake' | 'payment' | 'chat' | 'finalizing';

export interface UseDignityChatReturn {
  messages: DignityMessage[];
  phase: DignityPhase;
  intakePhase: IntakePhase;
  intakeData: Partial<IntakeData>;
  procedures: LocalScenarioProcedure[];
  scenarioName: string;
  messagesUsed: number;
  maxMessages: number;
  isStreaming: boolean;
  isBuilding: boolean;
  isFinishing: boolean;
  inputType: 'text' | 'zip';
  skipButton: string | undefined;
  sendMessage: (content: string) => void;
  handleQuickReply: (value: string) => void;
  handlePaymentComplete: (token: string) => void;
  handleSkip: () => void;
  finalize: () => Promise<FinalizedScenario | null>;
}

let msgId = 0;
function nextId() {
  return `dignity-${++msgId}-${Date.now()}`;
}

function createMessage(
  role: 'user' | 'assistant' | 'system',
  content: string,
  extra?: Partial<DignityMessage>
): DignityMessage {
  return {
    id: nextId(),
    role,
    content,
    timestamp: Date.now(),
    ...extra,
  };
}

// ── Mock AI response generator (reused from useAiChat) ──

function generateMockResponse(
  userMessage: string,
  intakeData: IntakeData,
  messageIndex: number,
  existingProcedures: LocalScenarioProcedure[]
): { text: string; procedures: LocalScenarioProcedure[] } {
  const situationLower = intakeData.situation.toLowerCase();
  const newProcedures: LocalScenarioProcedure[] = [];

  if (messageIndex === 0) {
    if (situationLower.includes('hip') || situationLower.includes('knee') || situationLower.includes('replacement')) {
      const joint = situationLower.includes('hip') ? 'hip' : 'knee';
      const mainCode = joint === 'hip' ? '27130' : '27447';
      const mainDesc = joint === 'hip' ? 'Total hip replacement (arthroplasty)' : 'Total knee replacement (arthroplasty)';

      newProcedures.push(
        { code: '99204', codeType: 'CPT', description: 'Pre-operative consultation', phase: 'Pre-Op', quantity: 1, estimatedCost: 350, setting: 'outpatient' },
        { code: mainCode, codeType: 'CPT', description: mainDesc, phase: 'Surgery', quantity: 1, estimatedCost: joint === 'hip' ? 24000 : 22000, setting: 'inpatient' },
        { code: '0360', codeType: 'REV', description: 'Operating room', phase: 'Surgery', quantity: 1, estimatedCost: 10000, setting: 'inpatient' },
      );

      return {
        text: `Let me analyze your ${joint} replacement costs...\n\nI've started building your scenario with the core procedure and pre-op consultation. Here's what I've found so far — and a few questions to make this more accurate:\n\n1. Has your doctor mentioned traditional or robotic-assisted surgery?\n2. How many days of hospital stay are they expecting?\n3. Have they discussed physical therapy plans?`,
        procedures: newProcedures,
      };
    }

    if (situationLower.includes('pregnancy') || situationLower.includes('baby') || situationLower.includes('deliver')) {
      newProcedures.push(
        { code: '59425', codeType: 'CPT', description: 'Antepartum care', phase: 'Prenatal Care', quantity: 1, estimatedCost: 2200, setting: 'outpatient' },
        { code: '76805', codeType: 'CPT', description: 'Pregnancy ultrasound', phase: 'Prenatal Care', quantity: 3, estimatedCost: 1200, setting: 'outpatient' },
        { code: '80055', codeType: 'CPT', description: 'Obstetric blood panel', phase: 'Prenatal Care', quantity: 2, estimatedCost: 400, setting: 'outpatient' },
      );

      return {
        text: `Congratulations! Let me help you understand the costs of your pregnancy and delivery.\n\nI've started with prenatal care essentials. To build a more complete picture:\n\n1. Is your doctor expecting a vaginal delivery or has a C-section been discussed?\n2. How far along are you currently?\n3. Are there any known complications or risk factors?`,
        procedures: newProcedures,
      };
    }

    // Includes cancer, HIV, chronic conditions, etc.
    if (situationLower.includes('cancer') || situationLower.includes('prostate') || situationLower.includes('chemo')) {
      newProcedures.push(
        { code: '99205', codeType: 'CPT', description: 'Comprehensive oncology consultation', phase: 'Diagnosis', quantity: 1, estimatedCost: 500, setting: 'outpatient' },
        { code: '88305', codeType: 'CPT', description: 'Tissue biopsy pathology', phase: 'Diagnosis', quantity: 1, estimatedCost: 350, setting: 'outpatient' },
        { code: '77386', codeType: 'CPT', description: 'Radiation treatment delivery (IMRT)', phase: 'Treatment', quantity: 20, estimatedCost: 12000, setting: 'outpatient' },
      );

      return {
        text: `I understand — dealing with cancer treatment costs is stressful enough without pricing surprises. Let me help you get a clear picture.\n\nI've started with initial diagnosis and radiation treatment codes. To refine this:\n\n1. What type and stage of cancer has been diagnosed?\n2. Has your doctor recommended surgery, chemo, radiation, or a combination?\n3. How long is the expected treatment timeline?`,
        procedures: newProcedures,
      };
    }

    if (situationLower.includes('hiv') || situationLower.includes('antiviral') || situationLower.includes('antiretroviral')) {
      newProcedures.push(
        { code: '99214', codeType: 'CPT', description: 'Follow-up office visit (established patient)', phase: 'Ongoing Care', quantity: 4, estimatedCost: 800, setting: 'outpatient' },
        { code: '86703', codeType: 'CPT', description: 'HIV antibody testing', phase: 'Monitoring', quantity: 2, estimatedCost: 200, setting: 'outpatient' },
        { code: '86360', codeType: 'CPT', description: 'CD4/CD8 count', phase: 'Monitoring', quantity: 4, estimatedCost: 600, setting: 'outpatient' },
      );

      return {
        text: `I'll help you understand the full cost picture for HIV management. This typically includes regular monitoring, medication, and specialist visits.\n\nI've started with the essential monitoring codes. To complete your scenario:\n\n1. Are you currently on antiretroviral therapy (e.g., Biktarvy, Descovy)?\n2. How often do you see your infectious disease specialist?\n3. Are there any co-occurring conditions we should include?`,
        procedures: newProcedures,
      };
    }

    // Generic fallback
    newProcedures.push(
      { code: '99204', codeType: 'CPT', description: 'Initial specialist consultation', phase: 'Consultation', quantity: 1, estimatedCost: 350, setting: 'outpatient' },
    );

    return {
      text: `Thank you for sharing your situation. I want to make sure I build an accurate cost estimate for you.\n\nI've started with an initial consultation. Could you tell me:\n\n1. What specific procedure or treatment has your doctor recommended?\n2. Will this require a hospital stay or is it outpatient?\n3. Have they mentioned any pre-operative tests or follow-up care?`,
      procedures: newProcedures,
    };
  }

  // Follow-up messages
  const msgLower = userMessage.toLowerCase();

  if (msgLower.includes('physical therapy') || msgLower.includes('pt') || msgLower.includes('rehab')) {
    newProcedures.push(
      { code: '97110', codeType: 'CPT', description: 'Physical therapy session', phase: 'Rehabilitation', quantity: 24, estimatedCost: 4800, setting: 'outpatient' },
      { code: '97140', codeType: 'CPT', description: 'Manual therapy', phase: 'Rehabilitation', quantity: 12, estimatedCost: 1200, setting: 'outpatient' },
    );

    return {
      text: `I've added physical therapy — typically 24 sessions over 3 months, plus manual therapy. This is a significant part of recovery.\n\nSome tips:\n- Many insurance plans cover PT with a copay per visit\n- Ask your doctor for a specific PT prescription to avoid surprise denials\n\nAnything else your doctor mentioned?`,
      procedures: newProcedures,
    };
  }

  if (msgLower.includes('anesthesia') || msgLower.includes('surgery') || msgLower.includes('robotic')) {
    if (!existingProcedures.some(p => p.code === '00840')) {
      newProcedures.push(
        { code: '00840', codeType: 'CPT', description: 'General anesthesia', phase: 'Surgery', quantity: 1, estimatedCost: 2500, setting: 'inpatient' },
      );
    }

    return {
      text: `I've added anesthesia to your surgery plan. Would you like me to also add:\n- Hospital room charges (typically 1-3 days)\n- Post-surgical medication management\n- Follow-up visits with your surgeon?`,
      procedures: newProcedures,
    };
  }

  if (msgLower.includes('c-section') || msgLower.includes('cesarean')) {
    newProcedures.push(
      { code: '59510', codeType: 'CPT', description: 'Cesarean delivery package', phase: 'Surgery & Delivery', quantity: 1, estimatedCost: 7500, setting: 'inpatient' },
      { code: '00840', codeType: 'CPT', description: 'Anesthesia for C-section', phase: 'Surgery & Delivery', quantity: 1, estimatedCost: 1800, setting: 'inpatient' },
      { code: '0360', codeType: 'REV', description: 'Operating room', phase: 'Surgery & Delivery', quantity: 1, estimatedCost: 12000, setting: 'inpatient' },
    );

    return {
      text: `I've added C-section delivery codes. C-sections usually require a 3-4 day hospital stay. Would you like me to add recovery room charges and post-op care?`,
      procedures: newProcedures,
    };
  }

  if (msgLower.includes('medication') || msgLower.includes('drug') || msgLower.includes('biktarvy') || msgLower.includes('antidepressant')) {
    newProcedures.push(
      { code: '99214', codeType: 'CPT', description: 'Medication management visit', phase: 'Ongoing Care', quantity: 4, estimatedCost: 800, setting: 'outpatient' },
    );

    return {
      text: `I've added medication management visits. Drug costs vary enormously — some medications can be found for 90% less at different pharmacies or with manufacturer coupons.\n\nWould you like me to add specific drug codes, or any other tests or procedures?`,
      procedures: newProcedures,
    };
  }

  if (msgLower.includes('follow') || msgLower.includes('after') || msgLower.includes('recovery')) {
    newProcedures.push(
      { code: '99213', codeType: 'CPT', description: 'Follow-up office visit', phase: 'Follow-Up', quantity: 4, estimatedCost: 800, setting: 'outpatient' },
    );

    return {
      text: `I've added follow-up visits — typically 4 visits over the next few months. Your scenario is looking comprehensive! Anything else, or ready to finalize?`,
      procedures: newProcedures,
    };
  }

  return {
    text: `That's helpful context. Your current estimate looks solid.\n\nWould you like me to:\n- Add any specific tests or imaging?\n- Include medication costs?\n- Add follow-up care visits?\n\nOr hit "Finalize Scenario" to generate your complete cost report.`,
    procedures: [],
  };
}

// ── Hook ──

export function useDignityChat(): UseDignityChatReturn {
  const [messages, setMessages] = useState<DignityMessage[]>([]);
  const [phase, setPhase] = useState<DignityPhase>('intake');
  const [intakePhase, setIntakePhase] = useState<IntakePhase>('greeting');
  const [intakeData, setIntakeData] = useState<Partial<IntakeData>>({});
  const [procedures, setProcedures] = useState<LocalScenarioProcedure[]>([]);
  const [scenarioName, setScenarioName] = useState('');
  const [messagesUsed, setMessagesUsed] = useState(0);
  const [isStreaming, setIsStreaming] = useState(false);
  const [isBuilding, setIsBuilding] = useState(false);
  const [isFinishing, setIsFinishing] = useState(false);
  const maxMessages = 15;
  const aiMessageIndex = useRef(0);

  // Initialize with greeting on first render
  const initialized = useRef(false);
  if (!initialized.current) {
    initialized.current = true;
    const question = getQuestion('greeting');
    if (question) {
      const msg = createMessage('assistant', question.getMessage({}), {
        quickReplies: question.quickReplies?.({}),
      });
      setMessages([msg]);
    }
  }

  // ── Advance intake to next question ──
  const advanceIntake = useCallback((newPartial: Partial<IntakeData>, currentPhase: IntakePhase) => {
    const next = getNextPhase(currentPhase);
    if (!next) return;

    setIntakePhase(next);

    const question = getQuestion(next);
    if (!question) return;

    // If this is the summary phase, transition to payment
    if (next === 'summary') {
      setPhase('payment');
    }

    const assistantMsg = createMessage('assistant', question.getMessage(newPartial), {
      quickReplies: question.quickReplies?.(newPartial),
      isPaymentGate: next === 'summary',
    });

    setMessages(prev => [...prev, assistantMsg]);
  }, []);

  // ── Process intake response ──
  const processIntakeResponse = useCallback((input: string, currentPhase: IntakePhase) => {
    const question = getQuestion(currentPhase);
    if (!question) return;

    // Parse input into IntakeData fields
    const parsed = question.parse(input, intakeData);
    const newPartial = { ...intakeData, ...parsed };
    setIntakeData(newPartial);

    // Advance to next question
    advanceIntake(newPartial, currentPhase);
  }, [intakeData, advanceIntake]);

  // ── Send message (handles both intake and chat phases) ──
  const sendMessage = useCallback((content: string) => {
    if (!content.trim()) return;

    // Add user message
    const userMsg = createMessage('user', content);
    setMessages(prev => [...prev, userMsg]);

    if (phase === 'intake') {
      // Process as intake response
      processIntakeResponse(content, intakePhase);
    } else if (phase === 'chat') {
      if (messagesUsed >= maxMessages || isStreaming) return;

      setMessagesUsed(prev => prev + 1);
      setIsStreaming(true);

      const fullIntake = buildIntakeData(intakeData);

      setTimeout(() => {
        const { text, procedures: newProcs } = generateMockResponse(
          content,
          fullIntake,
          aiMessageIndex.current + 1,
          procedures,
        );
        aiMessageIndex.current++;

        const assistantMsg = createMessage('assistant', text);
        setMessages(prev => [...prev, assistantMsg]);

        if (newProcs.length > 0) {
          setIsBuilding(true);
          setProcedures(prev => [...prev, ...newProcs]);
          setTimeout(() => setIsBuilding(false), 800);
        }

        setIsStreaming(false);
      }, 1200);
    }
  }, [phase, intakePhase, intakeData, messagesUsed, maxMessages, isStreaming, procedures, processIntakeResponse]);

  // ── Handle quick reply click ──
  const handleQuickReply = useCallback((value: string) => {
    // For intake, quick replies map directly to values
    if (phase === 'intake') {
      const question = getQuestion(intakePhase);
      if (!question) return;

      // Add user message showing what they clicked
      const quickReply = question.quickReplies?.(intakeData)?.find(qr => qr.value === value);
      const displayText = quickReply?.label || value;
      const userMsg = createMessage('user', displayText);
      setMessages(prev => [...prev, userMsg]);

      // For greeting phase, use the full value as situation text
      const inputValue = intakePhase === 'greeting' ? value : value;
      const parsed = question.parse(inputValue, intakeData);
      const newPartial = { ...intakeData, ...parsed };
      setIntakeData(newPartial);

      advanceIntake(newPartial, intakePhase);
    } else if (phase === 'chat') {
      sendMessage(value);
    }
  }, [phase, intakePhase, intakeData, advanceIntake, sendMessage]);

  // ── Handle payment complete ──
  const handlePaymentComplete = useCallback((token: string) => {
    setPhase('chat');

    const fullIntake = buildIntakeData(intakeData);
    const name = fullIntake.situation.length > 30
      ? fullIntake.situation.slice(0, 30).trim() + '...'
      : fullIntake.situation;
    setScenarioName(name);

    // Generate initial AI analysis
    const { text, procedures: newProcs } = generateMockResponse(
      '',
      fullIntake,
      0,
      [],
    );

    const systemMsg = createMessage('system', 'Payment confirmed. Starting AI analysis...');
    const assistantMsg = createMessage('assistant', text);

    setMessages(prev => [...prev, systemMsg, assistantMsg]);

    if (newProcs.length > 0) {
      setIsBuilding(true);
      setProcedures(newProcs);
      setTimeout(() => setIsBuilding(false), 1000);
    }
  }, [intakeData]);

  // ── Handle skip button ──
  const handleSkip = useCallback(() => {
    if (phase !== 'intake') return;

    const question = getQuestion(intakePhase);
    if (!question?.skipButton) return;

    // Add user message showing skip
    const userMsg = createMessage('user', question.skipButton);
    setMessages(prev => [...prev, userMsg]);

    // Use empty/default value
    const parsed = question.parse('', intakeData);
    const newPartial = { ...intakeData, ...parsed };
    setIntakeData(newPartial);

    advanceIntake(newPartial, intakePhase);
  }, [phase, intakePhase, intakeData, advanceIntake]);

  // ── Finalize scenario ──
  const finalize = useCallback(async (): Promise<FinalizedScenario | null> => {
    if (procedures.length === 0) return null;

    setIsFinishing(true);
    setPhase('finalizing');

    await new Promise(resolve => setTimeout(resolve, 1500));

    const fullIntake = buildIntakeData(intakeData);
    const totalCost = procedures.reduce((s, p) => s + p.estimatedCost * p.quantity, 0);
    const phases = Array.from(new Set(procedures.map(p => p.phase).filter(Boolean)));

    const result: FinalizedScenario = {
      name: scenarioName || 'AI-Generated Scenario',
      description: fullIntake.situation,
      duration: fullIntake.urgency === 'emergency' ? '1-7 days' : '1-6 months',
      procedures,
      tags: ['ai-generated'],
      severityLevel: fullIntake.urgency === 'emergency' ? 7 : 5,
      aiConsumerMessage: `Based on your situation, we estimate your total costs at approximately $${totalCost.toLocaleString()}. This includes ${procedures.length} procedures across ${phases.length} phases of care. These are median national prices — your actual costs may vary based on your location and insurance coverage.`,
      aiClinicalSummary: `Your care pathway includes ${phases.join(', ')}. The ${procedures.length} procedures identified represent the standard clinical pathway for your situation.`,
      aiCostAnalysis: `The estimated total of $${totalCost.toLocaleString()} breaks down across ${phases.length} phases. Hospital prices vary significantly — shopping around for portable services like labs and imaging could save 30-60%.`,
      aiPreparationGuide: `Before your procedure:\n- Get a written cost estimate from your provider\n- Verify insurance pre-authorization\n- Ask about cash-pay discounts if uninsured\n- Review all facility fees (not just the surgeon's fee)\n\nDuring treatment:\n- Keep copies of all bills and EOBs\n- Question any unexpected charges immediately\n\nAfter treatment:\n- Review your final bill line by line\n- Negotiate if charges exceed estimates\n- Ask about payment plans if needed`,
    };

    setIsFinishing(false);
    return result;
  }, [procedures, scenarioName, intakeData]);

  // Get current input type and skip button from active question
  const currentQuestion = getQuestion(intakePhase);

  return {
    messages,
    phase,
    intakePhase,
    intakeData,
    procedures,
    scenarioName,
    messagesUsed,
    maxMessages,
    isStreaming,
    isBuilding,
    isFinishing,
    inputType: phase === 'intake' ? (currentQuestion?.inputType || 'text') : 'text',
    skipButton: phase === 'intake' ? currentQuestion?.skipButton : undefined,
    sendMessage,
    handleQuickReply,
    handlePaymentComplete,
    handleSkip,
    finalize,
  };
}
